import React, { useState } from 'react';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { googleLinkConflictMessage } from '../utils/authPhone';
import { linkGoogleWithPopup } from '../utils/authLinking';

const LINKABLE_CONFLICT_CODES = new Set([
  'auth/credential-already-in-use',
  'auth/account-exists-with-different-credential',
  'auth/email-already-in-use',
]);

export default function LinkGoogleModal({ onLinked }) {
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const phoneLabel = auth.currentUser?.phoneNumber || 'your phone number';

  const finalizeLinkedSession = async () => {
    await auth.currentUser?.reload();

    const email = auth.currentUser?.email;
    if (email && auth.currentUser?.uid) {
      try {
        await api.updateVoiceConfig(auth.currentUser.uid, { email });
      } catch (_) {
        // Business profile may not exist yet.
      }
    }

    await refreshUser();
    if (onLinked) onLinked();
  };

  const handleLinkGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await linkGoogleWithPopup(auth.currentUser);
      await finalizeLinkedSession();
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google sign-in was cancelled. Try again when ready.');
      } else if (LINKABLE_CONFLICT_CODES.has(err.code)) {
        setError(googleLinkConflictMessage());
      } else {
        setError(err.message || 'Could not link Google. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'linear-gradient(135deg, #1d2840 0%, #2d4a7a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '40px 32px',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1d2840', marginBottom: '8px' }}>
          Link Your Gmail
        </h2>
        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '28px', lineHeight: '1.5' }}>
          You signed in with <strong>{phoneLabel}</strong>. Add Gmail so you can also sign in with
          Google on the business dashboard — same as linking your phone after email sign-in.
        </p>

        {error && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              color: '#dc2626',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '13px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={() => void handleLinkGoogle()}
          disabled={loading}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '12px',
            borderRadius: '30px',
            border: '1px solid #d1d5db',
            background: '#fff',
            color: '#1d2840',
            fontWeight: '600',
            fontSize: '15px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            marginBottom: '12px',
          }}
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            style={{ width: 20, height: 20 }}
          />
          {loading ? 'Connecting…' : 'Link Google Account'}
        </button>

        <p style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.5', textAlign: 'center' }}>
          Required to continue. Your Gmail is only used for sign-in — not shared publicly.
        </p>
      </div>
    </div>
  );
}
